/**
 * Kalender — month/week/day views over the Google-Calendar mirror.
 *
 * Custom shadcn-native build: shadcn primitives + date-fns for date math.
 * Picks up event-type colors and AI/verified flags from `useMirrorEvents`.
 * Clicking an event opens the existing verification dialog.
 */
import * as React from 'react';
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  differenceInMinutes,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { Bot, CheckCircle2, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input, Textarea } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../components/ui/toast';
import {
  tsToDate,
  useMirrorEvents,
  useUpdateMirrorVerification,
  type MirrorEventRow,
} from '../hooks/useMirror';
import { useEventTypes, type EventTypeRow } from '../hooks/useEventTypes';
import { useSyncCalendarNow } from '../hooks/useGoogleCalendar';
import { usePhoneSettings } from '../hooks/usePhoneSettings';
import { HeaderActions, HeaderActionsLeft } from '../components/HeaderActions';
import { cn } from '../lib/utils';
import type { BookingSlot } from '@cp3/shared';

type ViewMode = 'month' | 'week' | 'day' | 'slot';

const WEEK_OPTIONS = { weekStartsOn: 1 as const, locale: de };
const DEFAULT_COLOR = '#5eead4';

interface PositionedEvent {
  event: MirrorEventRow;
  start: Date;
  end: Date;
  durationMin: number;
  color: string;
  eventType: EventTypeRow | undefined;
}

export function CalendarPage() {
  const [view, setView] = React.useState<ViewMode>('week');
  const [cursor, setCursor] = React.useState<Date>(() => new Date());
  const [editing, setEditing] = React.useState<MirrorEventRow | null>(null);

  // Fetch a generous window so view changes don't trigger a refetch.
  const range = React.useMemo(() => {
    if (view === 'slot') {
      // Slot view is single-day; tighten the window so we don't pull
      // months of data we won't render.
      const from = startOfDay(cursor).getTime();
      const to = endOfDay(cursor).getTime();
      return { fromMs: from, toMs: to };
    }
    const from = startOfMonth(addMonths(cursor, -1));
    const to = endOfMonth(addMonths(cursor, 2));
    return { fromMs: from.getTime(), toMs: to.getTime() };
  }, [cursor, view]);

  const events = useMirrorEvents(range);
  const eventTypes = useEventTypes();
  const sync = useSyncCalendarNow();
  const phoneSettings = usePhoneSettings();
  const { toast } = useToast();

  const positioned = React.useMemo<PositionedEvent[]>(() => {
    const items = events.data ?? [];
    const types = eventTypes.data ?? [];
    return items.flatMap((e) => {
      const start = tsToDate(e.start);
      const end = tsToDate(e.end);
      if (!start || !end) return [];
      const eventType = types.find((t) => t.id === e.matchedEventTypeId);
      return [{
        event: e,
        start,
        end,
        durationMin: Math.max(15, differenceInMinutes(end, start)),
        color: eventType?.color ?? DEFAULT_COLOR,
        eventType,
      }];
    });
  }, [events.data, eventTypes.data]);

  function shift(direction: -1 | 1) {
    if (view === 'month') setCursor((c) => addMonths(c, direction));
    else if (view === 'week') setCursor((c) => addWeeks(c, direction));
    else setCursor((c) => addDays(c, direction));
  }

  const periodLabel = React.useMemo(() => {
    if (view === 'month') return format(cursor, 'LLLL yyyy', { locale: de });
    if (view === 'week') {
      const start = startOfWeek(cursor, WEEK_OPTIONS);
      const end = endOfWeek(cursor, WEEK_OPTIONS);
      const sameMonth = isSameMonth(start, end);
      return sameMonth
        ? `${format(start, 'd.', { locale: de })} – ${format(end, 'd. LLLL yyyy', { locale: de })}`
        : `${format(start, 'd. LLL', { locale: de })} – ${format(end, 'd. LLL yyyy', { locale: de })}`;
    }
    return format(cursor, 'EEEE, d. LLLL yyyy', { locale: de });
  }, [view, cursor]);

  const slots: BookingSlot[] = React.useMemo(() => {
    if (view !== 'slot') return [];
    const raw = (phoneSettings.query.data as Record<string, unknown> | null | undefined)?.bookingSlots;
    if (!raw || !Array.isArray(raw) || raw.length === 0) return [SLOT_FALLBACK];
    return raw as BookingSlot[];
  }, [view, phoneSettings.query.data]);

  return (
    // Fill the viewport top-to-bottom WITHOUT introducing a page
    // scrollbar. The SidebarInset wrapper consumes:
    //   • SiteHeader            56 px (h-14)
    //   • Inset top+bottom margin  16 px (md:m-2 = 8px × 2)
    //   • Inset min-h offset       16 px (calc(100svh - spacing.4))
    //   • Page padding             32-48 px (p-4 / md:p-6)
    // Total chrome ≈ 120 px on desktop, ≈ 88 px on mobile. We pick
    // 9rem (144 px) to leave a small safety buffer so rounding can't
    // bleed past the viewport edge, and `overflow-hidden` is the
    // belt-and-suspenders so even an off-by-one never spawns a
    // scrollbar on the page itself.
    // Page is rendered in the AppShell's "flush" frame (no outer
    // padding, no gap to the SiteHeader). Date-nav lives in the left
    // header slot, view-switch + sync in the right slot, and the grid
    // below fills the remaining viewport edge-to-edge.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Left header actions — desktop only; the mobile toolbar below
          covers the same controls in a narrower layout. */}
      <HeaderActionsLeft>
        <div className="hidden items-center gap-1 md:flex">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => shift(-1)}
            aria-label="Zurück"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => shift(1)}
            aria-label="Vor"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2"
            onClick={() => setCursor(new Date())}
          >
            Heute
          </Button>
          <span className="ml-1 whitespace-nowrap text-sm font-medium capitalize text-muted-foreground">
            {periodLabel}
          </span>
        </div>
      </HeaderActionsLeft>

      <HeaderActions>
        <div className="hidden items-center gap-2 md:flex">
          <Tabs value={view} onValueChange={(v) => setView(v as ViewMode)}>
            <TabsList className="h-8">
              <TabsTrigger value="month" className="h-7 px-2 text-xs">
                Monatsansicht
              </TabsTrigger>
              <TabsTrigger value="week" className="h-7 px-2 text-xs">
                Wochenansicht
              </TabsTrigger>
              <TabsTrigger value="day" className="h-7 px-2 text-xs">
                Tagesansicht
              </TabsTrigger>
              <TabsTrigger value="slot" className="h-7 px-2 text-xs">
                Freie Slots
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            onClick={async () => {
              try {
                const r = await sync.mutateAsync();
                toast({
                  title: 'Synchronisiert',
                  description: `${r.updated} aktualisiert`,
                });
              } catch (err) {
                toast({
                  title: 'Fehler',
                  description: (err as Error).message,
                  variant: 'destructive',
                });
              }
            }}
            disabled={sync.isPending}
            aria-label="Synchronisieren"
            title="Synchronisieren"
          >
            {sync.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </HeaderActions>

      {/* Desktop calendar surface — month / week / day / slot grid.
          Hidden on mobile; the dedicated phone layout below replaces it. */}
      <div className="relative hidden min-h-0 flex-1 flex-col md:flex">
        {view === 'month' ? (
          <MonthView
            cursor={cursor}
            events={positioned}
            onPick={(e) => setEditing(e)}
            onJumpToDay={(d) => {
              setCursor(d);
              setView('day');
            }}
          />
        ) : view === 'week' ? (
          <WeekView cursor={cursor} events={positioned} onPick={(e) => setEditing(e)} />
        ) : view === 'day' ? (
          <DayView cursor={cursor} events={positioned} onPick={(e) => setEditing(e)} />
        ) : (
          <SlotView
            cursor={cursor}
            slots={slots}
            events={events.data ?? []}
            eventTypes={eventTypes.data ?? []}
            onPick={(e) => setEditing(e)}
          />
        )}
        {events.isLoading && positioned.length === 0 && (
          <div className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-md border border-border bg-card/90 px-2 py-1 text-[10px] text-muted-foreground shadow-sm">
            <Loader2 className="size-3 animate-spin" /> Termine laden …
          </div>
        )}
      </div>

      {/* Mobile-only "agenda" view — month strip + day pills + ordered
          event cards with a red "now" indicator. Replaces the legacy
          mobile time-grid which was hard to scan on a phone. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:hidden">
        <MobileCalendar
          cursor={cursor}
          setCursor={setCursor}
          events={positioned}
          loading={events.isLoading}
          syncing={sync.isPending}
          onSync={async () => {
            try {
              const r = await sync.mutateAsync();
              toast({ title: 'Synchronisiert', description: `${r.updated} aktualisiert` });
            } catch (err) {
              toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
            }
          }}
          onPick={(e) => setEditing(e)}
        />
      </div>

      {editing && <VerificationDialog event={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

const SLOT_FALLBACK: BookingSlot = {
  id: 'standard',
  name: 'Standard',
  colorTone: 'slate',
  eventTypeIds: [],
};

/* -------- Mobile agenda view -------- */

/**
 * Phone-optimised calendar built like a chat / agenda app:
 *   • month-name header with prev/next-month chevrons
 *   • horizontally scrollable day-pill strip (the cursor is the
 *     selected pill; tapping a pill jumps the cursor)
 *   • chronological event list for the selected day, with a red
 *     "now" indicator inserted at the right spot when the cursor
 *     falls on today.
 *
 * Colours stay in our existing teal / event-type palette — we
 * deliberately don't introduce the violet / blue from the source
 * mock-up. Text colour on event cards auto-flips between near-black
 * and white based on background luminance so high-saturation custom
 * event-type colours stay readable.
 */
function MobileCalendar({
  cursor,
  setCursor,
  events,
  loading,
  syncing,
  onSync,
  onPick,
}: {
  cursor: Date;
  setCursor: (d: Date) => void;
  events: PositionedEvent[];
  loading: boolean;
  syncing: boolean;
  onSync: () => void;
  onPick: (e: MirrorEventRow) => void;
}) {
  const today = new Date();
  const isToday = isSameDay(cursor, today);

  const dayEvents = React.useMemo(
    () =>
      events
        .filter((e) => isSameDay(e.start, cursor))
        .sort((a, b) => a.start.getTime() - b.start.getTime()),
    [events, cursor],
  );

  // 7 day-pills centred on the cursor: 3 days back .. 3 days forward.
  const dayStrip = React.useMemo(() => {
    const arr: Date[] = [];
    for (let i = -3; i <= 3; i += 1) arr.push(addDays(cursor, i));
    return arr;
  }, [cursor]);

  // Build a single render-list interleaving events with the "now" marker
  // so the red line lands between the right two events without extra
  // logic in the JSX.
  type Row = { kind: 'event'; ev: PositionedEvent } | { kind: 'now' };
  const rows: Row[] = [];
  let nowDone = false;
  for (const ev of dayEvents) {
    if (isToday && !nowDone && today < ev.start) {
      rows.push({ kind: 'now' });
      nowDone = true;
    }
    rows.push({ kind: 'event', ev });
  }
  if (isToday && !nowDone) rows.push({ kind: 'now' });

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-4">
      {/* ── Month header ── */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, -1))}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" />
          <span className="capitalize">
            {format(addMonths(cursor, -1), 'LLL', { locale: de })}
          </span>
        </button>
        <h2 className="text-xl font-bold capitalize">
          {format(cursor, 'LLLL', { locale: de })}
        </h2>
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, 1))}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <span className="capitalize">
            {format(addMonths(cursor, 1), 'LLL', { locale: de })}
          </span>
          <ChevronRight className="size-3.5" />
        </button>
      </div>

      {/* ── Day strip ── */}
      <div className="-mx-4 overflow-x-auto px-4">
        <div className="flex gap-2.5 pb-1">
          {dayStrip.map((d) => (
            <DayPill
              key={d.toISOString()}
              date={d}
              selected={isSameDay(d, cursor)}
              isToday={isSameDay(d, today)}
              onClick={() => setCursor(d)}
            />
          ))}
        </div>
      </div>

      {/* ── Section header (today / weekday) + sync icon ── */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold capitalize">
          {isToday ? 'Heute' : format(cursor, 'EEEE, d. LLL', { locale: de })}
        </h3>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          onClick={onSync}
          disabled={syncing}
          aria-label="Synchronisieren"
        >
          {syncing ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
        </Button>
      </div>

      {/* ── Event list ── */}
      {loading && dayEvents.length === 0 ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Lade Termine …
        </div>
      ) : dayEvents.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Keine Termine an diesem Tag.
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((r, i) =>
            r.kind === 'now' ? (
              <li key={`now-${i}`}>
                <NowRow />
              </li>
            ) : (
              <li key={r.ev.event.id}>
                <MobileEventCard ev={r.ev} onPick={() => onPick(r.ev.event)} />
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

function DayPill({
  date,
  selected,
  isToday,
  onClick,
}: {
  date: Date;
  selected: boolean;
  isToday: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-14 shrink-0 flex-col items-center justify-center rounded-2xl border px-2 py-3 transition-colors',
        selected
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : 'border-border bg-card text-foreground hover:bg-secondary',
      )}
      aria-pressed={selected}
      aria-label={format(date, 'EEEE, d. LLLL', { locale: de })}
    >
      <span className="text-xl font-bold tabular-nums leading-none">
        {format(date, 'd')}
      </span>
      <span
        className={cn(
          'mt-1 text-[10px] uppercase tracking-wider',
          selected ? 'text-primary-foreground/85' : 'text-muted-foreground',
        )}
      >
        {format(date, 'EEE', { locale: de })}
      </span>
      {!selected && isToday && (
        <span className="mt-1 size-1 rounded-full bg-primary" aria-hidden />
      )}
    </button>
  );
}

function MobileEventCard({
  ev,
  onPick,
}: {
  ev: PositionedEvent;
  onPick: () => void;
}) {
  const fg = pickReadableForeground(ev.color);
  const subFg = fg === '#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.7)';
  return (
    <div className="flex items-stretch gap-3">
      <div
        className="flex w-12 shrink-0 flex-col justify-between py-1 text-right text-[11px] tabular-nums text-muted-foreground"
        aria-hidden
      >
        <span>{format(ev.start, 'HH:mm')}</span>
        <span>{format(ev.end, 'HH:mm')}</span>
      </div>
      <button
        type="button"
        onClick={onPick}
        className="flex-1 rounded-2xl px-4 py-3 text-left shadow-sm transition-transform active:scale-[0.99]"
        style={{ backgroundColor: ev.color, color: fg }}
      >
        <div className="flex items-center gap-2">
          {ev.event.bookedByAi && (
            <Bot className="size-3 shrink-0" style={{ color: subFg }} aria-label="KI-Buchung" />
          )}
          {ev.event.verified && (
            <CheckCircle2
              className="size-3 shrink-0"
              style={{ color: subFg }}
              aria-label="Verifiziert"
            />
          )}
          <span className="truncate text-sm font-semibold">{ev.event.summary}</span>
        </div>
        {ev.eventType && (
          <div className="mt-0.5 truncate text-xs" style={{ color: subFg }}>
            {ev.eventType.name}
          </div>
        )}
        <div className="mt-1.5 text-[11px] tabular-nums" style={{ color: subFg }}>
          {format(ev.start, 'HH:mm')} – {format(ev.end, 'HH:mm')}
        </div>
      </button>
    </div>
  );
}

function NowRow() {
  const now = new Date();
  return (
    <div className="flex items-center gap-3">
      <div className="w-12 shrink-0 text-right text-[11px] tabular-nums text-rose-500">
        {format(now, 'HH:mm')}
      </div>
      <div className="flex flex-1 items-center gap-1">
        <span className="size-2 shrink-0 rounded-full bg-rose-500" aria-hidden />
        <span className="h-px flex-1 bg-rose-500" aria-hidden />
      </div>
    </div>
  );
}

/**
 * Returns "#ffffff" or near-black depending on the background's
 * relative luminance, so white text stays readable on dark event-type
 * colours and switches to dark text for pastel / very-light ones.
 */
function pickReadableForeground(bg: string): string {
  try {
    const hex = bg.startsWith('#') ? bg.slice(1) : bg;
    if (hex.length !== 6) return '#ffffff';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.62 ? '#0f172a' : '#ffffff';
  } catch {
    return '#ffffff';
  }
}

/* -------- Month view -------- */

function MonthView({
  cursor,
  events,
  onPick,
  onJumpToDay,
}: {
  cursor: Date;
  events: PositionedEvent[];
  onPick: (e: MirrorEventRow) => void;
  onJumpToDay: (d: Date) => void;
}) {
  const start = startOfWeek(startOfMonth(cursor), WEEK_OPTIONS);
  const end = endOfWeek(endOfMonth(cursor), WEEK_OPTIONS);
  const days: Date[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);

  const today = new Date();
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    format(addDays(start, i), 'EEE', { locale: de }),
  );

  // 6 weeks × 7 days. Each row gets `1fr` so the month view perfectly
  // fills the parent height — no fixed min-height per cell, no empty
  // space below the grid. Cells with many events scroll internally.
  const rowCount = Math.ceil(days.length / 7);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border">
      <div className="grid grid-cols-7 border-b border-border bg-muted/30 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {weekdayLabels.map((label) => (
          <div key={label} className="px-2 py-1.5">{label}</div>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1 grid-cols-7"
        style={{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }}
      >
        {days.map((day) => {
          const dayEvents = events
            .filter((e) => isSameDay(e.start, day))
            .sort((a, b) => a.start.getTime() - b.start.getTime());
          const inMonth = isSameMonth(day, cursor);
          const isToday = isSameDay(day, today);
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'group flex min-h-0 flex-col gap-1 overflow-hidden border-b border-r border-border p-1.5 last:border-r-0',
                !inMonth && 'bg-muted/10 text-muted-foreground',
              )}
            >
              <button
                type="button"
                onClick={() => onJumpToDay(day)}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center self-end rounded-full text-xs font-medium transition-colors hover:bg-accent',
                  isToday && 'bg-primary text-primary-foreground hover:bg-primary',
                )}
                aria-label={`Tag ${format(day, 'd. LLLL', { locale: de })} öffnen`}
              >
                {format(day, 'd')}
              </button>
              <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-0.5">
                {dayEvents.map((e) => (
                  <EventChip key={e.event.id} ev={e} compact onClick={() => onPick(e.event)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------- Week view -------- */

const WEEK_START_HOUR = 7;
const WEEK_END_HOUR = 21;
// Total minutes covered by the time grid (7:00 → 21:00 = 14 hours = 840 minutes).
const TOTAL_GRID_MIN = (WEEK_END_HOUR - WEEK_START_HOUR) * 60;

function WeekView({
  cursor,
  events,
  onPick,
}: {
  cursor: Date;
  events: PositionedEvent[];
  onPick: (e: MirrorEventRow) => void;
}) {
  const weekStart = startOfWeek(cursor, WEEK_OPTIONS);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  return <TimeGrid days={days} events={events} onPick={onPick} />;
}

/* -------- Day view -------- */

function DayView({
  cursor,
  events,
  onPick,
}: {
  cursor: Date;
  events: PositionedEvent[];
  onPick: (e: MirrorEventRow) => void;
}) {
  return <TimeGrid days={[cursor]} events={events} onPick={onPick} />;
}

/* -------- Slot view (resource grid: one column per booking-slot) -------- */

const SLOT_TONE_BG: Record<BookingSlot['colorTone'], string> = {
  slate: 'bg-slate-500/[0.04]',
  sky: 'bg-sky-500/[0.04]',
  emerald: 'bg-emerald-500/[0.04]',
  amber: 'bg-amber-500/[0.04]',
  rose: 'bg-rose-500/[0.04]',
  violet: 'bg-violet-500/[0.04]',
};
const SLOT_TONE_BORDER: Record<BookingSlot['colorTone'], string> = {
  slate: 'border-l-slate-500/30',
  sky: 'border-l-sky-500/30',
  emerald: 'border-l-emerald-500/30',
  amber: 'border-l-amber-500/30',
  rose: 'border-l-rose-500/30',
  violet: 'border-l-violet-500/30',
};
const SLOT_TONE_EVENT: Record<BookingSlot['colorTone'], string> = {
  slate: 'bg-slate-500/15 border-slate-400/40',
  sky: 'bg-sky-500/15 border-sky-400/40',
  emerald: 'bg-emerald-500/15 border-emerald-400/40',
  amber: 'bg-amber-500/15 border-amber-400/40',
  rose: 'bg-rose-500/15 border-rose-400/40',
  violet: 'bg-violet-500/15 border-violet-400/40',
};

function SlotView({
  cursor,
  slots,
  events,
  eventTypes,
  onPick,
}: {
  cursor: Date;
  slots: BookingSlot[];
  events: MirrorEventRow[];
  eventTypes: EventTypeRow[];
  onPick: (e: MirrorEventRow) => void;
}) {
  const eventTypeMap = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const et of eventTypes) m.set(et.id, et.name);
    return m;
  }, [eventTypes]);

  // Group events into slots once. Events whose `slotId` matches a known
  // slot land in that column; everything else falls into the first
  // slot — same heuristic as the legacy CalendarDayPage so behaviour
  // is unchanged after the consolidation.
  const eventsBySlot = React.useMemo(() => {
    const slotIds = new Set(slots.map((s) => s.id));
    const out = new Map<string, MirrorEventRow[]>();
    for (const s of slots) out.set(s.id, []);
    const fallbackId = slots[0]?.id;
    if (!fallbackId) return out;
    const dayStart = startOfDay(cursor).getTime();
    const dayEnd = endOfDay(cursor).getTime();
    for (const ev of events) {
      const start = tsToDate(ev.start)?.getTime();
      if (start == null || start < dayStart || start > dayEnd) continue;
      const key = ev.slotId && slotIds.has(ev.slotId) ? ev.slotId : fallbackId;
      out.get(key)!.push(ev);
    }
    for (const list of out.values()) {
      list.sort((a, b) => {
        const sa = tsToDate(a.start)?.getTime() ?? 0;
        const sb = tsToDate(b.start)?.getTime() ?? 0;
        return sa - sb;
      });
    }
    return out;
  }, [slots, events, cursor]);

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden rounded-md border border-border">
      {/* Sticky time axis */}
      <div className="shrink-0 border-r border-border bg-card">
        <div className="h-9 border-b border-border" aria-hidden />
        <div className="relative w-12 sm:w-14 flex-1" style={{ height: '100%' }}>
          <TimeAxis />
        </div>
      </div>

      {/* Slot columns */}
      <div className="flex flex-1 snap-x snap-mandatory overflow-x-auto sm:snap-none">
        {slots.map((slot) => (
          <SlotColumn
            key={slot.id}
            slot={slot}
            events={eventsBySlot.get(slot.id) ?? []}
            eventTypeMap={eventTypeMap}
            columnCount={slots.length}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
}

const SLOT_START_HOUR = 7;
const SLOT_END_HOUR = 21;
const SLOT_TOTAL_MIN = (SLOT_END_HOUR - SLOT_START_HOUR) * 60;

function TimeAxis() {
  return (
    <div className="relative h-full">
      {Array.from({ length: SLOT_END_HOUR - SLOT_START_HOUR + 1 }).map((_, i) => {
        const hour = SLOT_START_HOUR + i;
        const topPct = (i * 60 / SLOT_TOTAL_MIN) * 100;
        return (
          <div
            key={hour}
            className="absolute left-0 right-0 -mt-2 px-1 text-right text-[10px] tabular-nums text-muted-foreground"
            style={{ top: `${topPct}%` }}
          >
            {String(hour).padStart(2, '0')}:00
          </div>
        );
      })}
    </div>
  );
}

function SlotColumn({
  slot,
  events,
  eventTypeMap,
  columnCount,
  onPick,
}: {
  slot: BookingSlot;
  events: MirrorEventRow[];
  eventTypeMap: Map<string, string>;
  columnCount: number;
  onPick: (e: MirrorEventRow) => void;
}) {
  // Mobile snap-scroll behaviour identical to CalendarDayPage so muscle
  // memory carries over: each column ~85vw, snap-start.
  const widthClass =
    columnCount <= 1
      ? 'w-full'
      : 'w-[85vw] min-w-[200px] flex-shrink-0 snap-start sm:w-auto sm:flex-1 sm:min-w-[200px]';

  return (
    <div className={cn('relative flex flex-col border-l', widthClass, SLOT_TONE_BORDER[slot.colorTone])}>
      <div
        className={cn(
          'sticky top-0 z-10 flex h-9 items-center gap-2 border-b border-border bg-card px-3 text-sm font-medium',
          SLOT_TONE_BG[slot.colorTone],
        )}
      >
        <span className="truncate">{slot.name}</span>
        <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">{events.length}×</span>
      </div>
      <div className={cn('relative flex-1', SLOT_TONE_BG[slot.colorTone])}>
        {/* Hour separator lines (full hours solid, half hours faint) */}
        {Array.from({ length: SLOT_END_HOUR - SLOT_START_HOUR + 1 }).map((_, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 border-t border-border/40"
            style={{ top: `${(i * 60 / SLOT_TOTAL_MIN) * 100}%` }}
            aria-hidden
          />
        ))}
        {Array.from({ length: SLOT_END_HOUR - SLOT_START_HOUR }).map((_, i) => (
          <div
            key={`half-${i}`}
            className="absolute left-0 right-0 border-t border-border/15"
            style={{ top: `${((i * 60 + 30) / SLOT_TOTAL_MIN) * 100}%` }}
            aria-hidden
          />
        ))}
        {events.map((ev) => (
          <SlotEventBlock
            key={ev.id}
            event={ev}
            slot={slot}
            eventTypeMap={eventTypeMap}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
}

function SlotEventBlock({
  event,
  slot,
  eventTypeMap,
  onPick,
}: {
  event: MirrorEventRow;
  slot: BookingSlot;
  eventTypeMap: Map<string, string>;
  onPick: (e: MirrorEventRow) => void;
}) {
  const start = tsToDate(event.start);
  const end = tsToDate(event.end);
  if (!start || !end) return null;
  const minutesFromStart = (start.getHours() - SLOT_START_HOUR) * 60 + start.getMinutes();
  if (minutesFromStart < 0 || minutesFromStart > SLOT_TOTAL_MIN) return null;
  const durationMin = Math.max(15, (end.getTime() - start.getTime()) / 60_000);
  const topPct = (minutesFromStart / SLOT_TOTAL_MIN) * 100;
  const heightPct = Math.min(
    (durationMin / SLOT_TOTAL_MIN) * 100,
    100 - topPct,
  );
  const eventTypeName = event.matchedEventTypeId
    ? eventTypeMap.get(event.matchedEventTypeId) ?? null
    : null;

  return (
    <button
      type="button"
      onClick={() => onPick(event)}
      className={cn(
        'absolute left-1 right-1 overflow-hidden rounded-md border px-2 py-1 text-left text-[11px] leading-tight shadow-sm transition-transform hover:-translate-y-px hover:shadow-md',
        SLOT_TONE_EVENT[slot.colorTone],
      )}
      style={{ top: `${topPct}%`, height: `${heightPct}%` }}
      title={`${formatSlotTimeRange(start, end)} · ${event.summary}`}
    >
      <div className="flex items-center gap-1 truncate font-medium text-foreground">
        {event.bookedByAi && <Bot className="h-2.5 w-2.5 shrink-0" />}
        {event.verified && <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-emerald-400" />}
        {formatSlotTimeRange(start, end)}
      </div>
      <div className="truncate text-muted-foreground">{event.summary}</div>
      {eventTypeName && (
        <div className="mt-0.5 truncate text-[9px] uppercase tracking-wider text-muted-foreground">
          {eventTypeName}
        </div>
      )}
    </button>
  );
}

function formatSlotTimeRange(start: Date, end: Date): string {
  const fmt = (d: Date) =>
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${fmt(start)}–${fmt(end)}`;
}

/* -------- Shared time-grid (week + day) -------- */

function TimeGrid({
  days,
  events,
  onPick,
}: {
  days: Date[];
  events: PositionedEvent[];
  onPick: (e: MirrorEventRow) => void;
}) {
  const today = new Date();
  // 14 hour-row intervals between hours 7 and 21, e.g. row 0 spans 7:00→8:00.
  const rowHours = Array.from(
    { length: WEEK_END_HOUR - WEEK_START_HOUR },
    (_, i) => WEEK_START_HOUR + i,
  );

  return (
    // The time-grid scales to whatever vertical space the parent gives
    // it. Hour rows are CSS-grid `1fr` so they always fill exactly one
    // share of the available height; events are positioned by
    // percentage so their height equals their real-time duration as a
    // fraction of the visible window. This means a 30-min event is
    // exactly 1/28 of the grid, regardless of monitor size.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border">
      <div
        className="grid shrink-0 border-b border-border bg-muted/30"
        style={{ gridTemplateColumns: `60px repeat(${days.length}, minmax(0, 1fr))` }}
      >
        <div />
        {days.map((day) => {
          const isToday = isSameDay(day, today);
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'flex flex-col items-center gap-0.5 px-2 py-1.5 text-xs',
                isToday && 'text-primary',
              )}
            >
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {format(day, 'EEE', { locale: de })}
              </span>
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold',
                  isToday && 'bg-primary text-primary-foreground',
                )}
              >
                {format(day, 'd')}
              </span>
            </div>
          );
        })}
      </div>

      <div
        className="relative grid min-h-0 flex-1"
        style={{
          gridTemplateColumns: `60px repeat(${days.length}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rowHours.length}, minmax(0, 1fr))`,
        }}
      >
        {/* Hour-label column. Each label sits in its own grid row so
            it visually aligns with the start of that hour interval. */}
        {rowHours.map((h, i) => (
          <div
            key={`label-${h}`}
            className="flex justify-end border-r border-border pr-2 pt-1 text-[10px] font-medium text-muted-foreground"
            style={{ gridColumn: 1, gridRow: i + 1 }}
          >
            {String(h).padStart(2, '0')}:00
          </div>
        ))}

        {/* Hour separator lines spanning all day columns. We render
            them as their own grid cells so the dashed half-hour mark
            still aligns regardless of overall grid height. */}
        {rowHours.map((h, i) => (
          <div
            key={`sep-${h}`}
            className={cn(
              'pointer-events-none border-b border-border/60',
              i % 2 === 1 && 'border-dashed',
            )}
            style={{
              gridColumn: `2 / span ${days.length}`,
              gridRow: i + 1,
            }}
          />
        ))}

        {days.map((day, dayIdx) => {
          const dayStart = startOfDay(day);
          const gridStart = addHours(dayStart, WEEK_START_HOUR);
          const gridEnd = addHours(dayStart, WEEK_END_HOUR);
          const dayEvents = events.filter((e) =>
            isWithinInterval(e.start, { start: dayStart, end: endOfDay(day) }) ||
            isWithinInterval(e.end, { start: dayStart, end: endOfDay(day) }) ||
            (e.start <= dayStart && e.end >= endOfDay(day)),
          );
          const isToday = isSameDay(day, today);
          const nowMin = isToday ? differenceInMinutes(today, gridStart) : -1;
          const showNow = nowMin >= 0 && nowMin <= TOTAL_GRID_MIN;

          return (
            <div
              key={day.toISOString()}
              className={cn(
                'relative border-r border-border last:border-r-0',
                isToday && 'bg-primary/[0.04]',
              )}
              style={{
                gridColumn: dayIdx + 2,
                gridRow: `1 / span ${rowHours.length}`,
              }}
            >
              {showNow && (
                <div
                  className="pointer-events-none absolute left-0 right-0 z-10 flex items-center"
                  style={{ top: `${(nowMin / TOTAL_GRID_MIN) * 100}%` }}
                >
                  <span className="h-2 w-2 -translate-x-1 rounded-full bg-rose-500" />
                  <span className="h-px flex-1 bg-rose-500" />
                </div>
              )}
              {layoutColumns(dayEvents, gridStart, gridEnd).map(({ ev, lane, lanes }) => {
                // Clip to the visible window in case the event spans
                // before/after the displayed hours.
                const visStart = ev.start < gridStart ? gridStart : ev.start;
                const visEnd = ev.end > gridEnd ? gridEnd : ev.end;
                const startMin = differenceInMinutes(visStart, gridStart);
                const durMin = Math.max(1, differenceInMinutes(visEnd, visStart));
                const topPct = (startMin / TOTAL_GRID_MIN) * 100;
                // Height matches duration EXACTLY — no min-height
                // floor — so a 60-min slot is twice the size of a
                // 30-min slot. The user explicitly asked for this.
                const heightPct = (durMin / TOTAL_GRID_MIN) * 100;
                const widthPct = 100 / lanes;
                return (
                  <button
                    key={`${day.toISOString()}-${ev.event.id}-${dayIdx}`}
                    type="button"
                    onClick={() => onPick(ev.event)}
                    className="absolute overflow-hidden rounded-md border px-1.5 py-1 text-left text-[11px] leading-tight shadow-sm transition-transform hover:-translate-y-px hover:shadow-md"
                    style={{
                      top: `${topPct}%`,
                      height: `calc(${heightPct}% - 2px)`,
                      left: `calc(${lane * widthPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      background: `${ev.color}26`,
                      borderColor: `${ev.color}80`,
                      color: ev.color,
                    }}
                  >
                    <div className="flex items-center gap-1 truncate">
                      {ev.event.bookedByAi && <Bot className="h-2.5 w-2.5 shrink-0" />}
                      {ev.event.verified && <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-emerald-400" />}
                      <span className="truncate font-semibold text-foreground">{ev.event.summary}</span>
                    </div>
                    <div className="truncate text-[10px] text-muted-foreground">
                      {format(ev.start, 'HH:mm')}–{format(ev.end, 'HH:mm')}
                      {ev.eventType && ` · ${ev.eventType.name}`}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Greedy column layout: places overlapping events side-by-side in lanes.
 * Returns each visible event with its assigned lane index and total lane count
 * within its overlap cluster.
 */
function layoutColumns(
  dayEvents: PositionedEvent[],
  gridStart: Date,
  gridEnd: Date,
): Array<{ ev: PositionedEvent; lane: number; lanes: number }> {
  const visible = dayEvents
    .filter((e) => e.end > gridStart && e.start < gridEnd)
    .sort((a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime());

  const lanes: Array<{ end: Date }> = [];
  const placed: Array<{ ev: PositionedEvent; lane: number }> = [];

  for (const ev of visible) {
    let laneIdx = lanes.findIndex((l) => l.end <= ev.start);
    if (laneIdx === -1) {
      laneIdx = lanes.length;
      lanes.push({ end: ev.end });
    } else {
      lanes[laneIdx] = { end: ev.end };
    }
    placed.push({ ev, lane: laneIdx });
  }

  // For each cluster, compute the max simultaneous lane count.
  const result: Array<{ ev: PositionedEvent; lane: number; lanes: number }> = [];
  for (const p of placed) {
    let lanes = 1;
    for (const q of placed) {
      if (p.ev.end > q.ev.start && p.ev.start < q.ev.end) {
        lanes = Math.max(lanes, q.lane + 1);
      }
    }
    result.push({ ...p, lanes });
  }
  return result;
}

/* -------- Event chip (month view) -------- */

function EventChip({
  ev,
  compact,
  onClick,
}: {
  ev: PositionedEvent;
  compact?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-1 rounded px-1.5 py-0.5 text-left text-[10px] transition-colors"
      style={{
        background: `${ev.color}26`,
        color: ev.color,
      }}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: ev.color }} />
      {ev.event.bookedByAi && <Bot className="h-2.5 w-2.5 shrink-0" />}
      {ev.event.verified && <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-emerald-400" />}
      <span className="truncate font-medium text-foreground">
        {!compact && `${format(ev.start, 'HH:mm')} `}
        {ev.event.summary}
      </span>
    </button>
  );
}

/* -------- Verification dialog (kept from previous CalendarPage) -------- */

function VerificationDialog({ event, onClose }: { event: MirrorEventRow; onClose: () => void }) {
  const update = useUpdateMirrorVerification();
  const eventTypes = useEventTypes();
  const { toast } = useToast();
  const start = tsToDate(event.start);
  const end = tsToDate(event.end);
  const plannedDur = start && end ? Math.round((end.getTime() - start.getTime()) / 60_000) : 0;
  const eventType = eventTypes.data?.find((t) => t.id === event.matchedEventTypeId);

  const [verified, setVerified] = React.useState(event.verified ?? false);
  const [actualDur, setActualDur] = React.useState(event.actualDurationMinutes ?? plannedDur);
  const [score, setScore] = React.useState(event.effectivenessScore ?? 70);
  const [notes, setNotes] = React.useState(event.notes ?? '');

  async function onSave() {
    try {
      await update.mutateAsync({
        eventId: event.id,
        verified,
        actualDurationMinutes: actualDur,
        effectivenessScore: score,
        notes,
      });
      toast({ title: 'Verifikation gespeichert' });
      onClose();
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {event.bookedByAi && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                <Bot className="h-2.5 w-2.5" /> AI
              </span>
            )}
            {event.summary}
          </DialogTitle>
          <DialogDescription>
            {start ? format(start, "EEEE, d. LLLL yyyy 'um' HH:mm", { locale: de }) : '—'} · geplant {plannedDur} min
            {eventType && ` · ${eventType.name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <Label>Verifiziert</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Nur verifizierte Termine fließen ins Effektivitäts-Dashboard ein.
              </p>
            </div>
            <Switch checked={verified} onCheckedChange={setVerified} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="actual">Tatsächliche Dauer (min)</Label>
            <Input
              id="actual"
              type="number"
              min={1}
              max={720}
              value={actualDur}
              onChange={(e) => setActualDur(Number(e.target.value))}
            />
            {eventType && actualDur !== eventType.durationMinutes && (
              <p className="text-xs text-amber-400">
                Geplant {eventType.durationMinutes} min, tatsächlich {actualDur} min.
                {Math.abs(actualDur - eventType.durationMinutes) >= 10 &&
                  ' Erwäge, den Termin-Typ entsprechend anzupassen.'}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Effektivitäts-Score: {score}</Label>
            <Slider value={[score]} min={0} max={100} step={5} onValueChange={(v) => setScore(v[0] ?? score)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Termin hätte 45 min gebraucht, Klient war sehr ausführlich."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={onSave} disabled={update.isPending}>
            {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
